/**
 * Auth Service
 *
 * Handles Google OAuth and email/password login via the Cloudflare Worker.
 * Session is stored in an HttpOnly cookie set by the Worker — the frontend
 * never touches the raw JWT. All auth state is restored via /auth/me on load.
 */

export interface User {
  id: string;
  email: string;
  name: string;
  picture_url: string | null;
  provider: 'google' | 'email';
  /** User tier — 'pro' for all users during the launch period, 'free' once limits apply. */
  tier: 'free' | 'pro';
  /** True for the first 100 users ever registered — permanent badge. */
  is_founding_member: boolean;
}

export interface AuthResult {
  ok: boolean;
  error?: string;
}

// PROD: use relative /api path — see BACKEND_URL in app.config.ts for proxy rationale
const AUTH_BASE = import.meta.env.PROD
  ? '/api'
  : 'http://localhost:8787';

export class AuthService {
  private user: User | null = null;

  /**
   * Called on page load. Sends the session cookie to /auth/me to restore
   * the logged-in user. Returns null silently if not logged in.
   */
  async init(): Promise<User | null> {
    try {
      // Cookie is sent automatically — no token handling needed in frontend
      const res = await fetch(`${AUTH_BASE}/auth/me`, { credentials: 'include' });
      if (!res.ok) return null;
      this.user = (await res.json()) as User;
      return this.user;
    } catch {
      return null;
    }
  }

  /**
   * Redirects the browser to the Worker's /auth/google route, which
   * initiates the Google OAuth flow.
   */
  login(): void {
    window.location.href = `${AUTH_BASE}/auth/google`;
  }

  /**
   * Logs in with email and password. On success, refreshes user state from /auth/me.
   */
  async loginWithEmail(email: string, password: string): Promise<AuthResult> {
    try {
      const res = await fetch(`${AUTH_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        return { ok: false, error: data.error ?? 'Login failed' };
      }
      await this.init();
      return { ok: true };
    } catch {
      return { ok: false, error: 'Network error' };
    }
  }

  /**
   * Registers a new user with email and password. On success, refreshes user state.
   */
  async registerWithEmail(email: string, password: string): Promise<AuthResult> {
    try {
      const res = await fetch(`${AUTH_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        return { ok: false, error: data.error ?? 'Registration failed' };
      }
      await this.init();
      return { ok: true };
    } catch {
      return { ok: false, error: 'Network error' };
    }
  }

  /**
   * Calls /auth/logout to clear the session cookie server-side.
   */
  async logout(): Promise<void> {
    await fetch(`${AUTH_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      // Silently swallow — logout must never break the app
    });
    this.user = null;
  }

  getUser(): User | null {
    return this.user;
  }
}
